/**
 * MonitoreoTab — Auditoría del pipeline de elegibilidad de reuniones.
 * Consume GET /api/meetings/pipeline-events desde MeetingService.
 * Solo visible para AdminGlobal.
 */

import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '@/contexts/UserContext';
import { useRecalculateResponseCounts } from '@/hooks/useSurveys';
import { ParticipantDetailTable } from '../shared/ParticipantDetailTable';
import './MonitoreoTab.css';

// ─── Types (espejo del backend PipelineEvent) ─────────────────────────────────

interface ParticipantDetail {
  userId: string;
  ouId?: string;
  timezone: string;
  surveysToday: number;
  surveysThisWeek: number;
  hoursSinceLastEligibleMeeting?: number;
  isEligible: boolean;
  reason?: string;
  includedInSample: boolean;
  samplingReason?: string;
}

interface PipelineEvent {
  id: string;
  meetingId: string;
  tenantId: string;
  createdAt: string;
  meetingStartUtc: string;
  durationMinutes: number;
  isMeetingEligible: boolean;
  meetingViolations: string[];
  participantDetails: ParticipantDetail[];
  totalEvaluated: number;
  totalEligible: number;
  totalSampled: number;
  maxPaxApplied: boolean;
}

interface SurveyInstanceDto {
  id: string;
  surveyName: string;
  status: number;       // 0 Pending | 1 Responded | 2 Omitted | 3 Expired
  expiresAt: string;
  meetingId: string;
  meetingSubject?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MEETING_URL = import.meta.env.VITE_API_MEETING_URL ?? '';
const SURVEY_URL  = import.meta.env.VITE_API_SURVEY_URL  ?? '';

function shortId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

function fmtDate(iso: string): string {
  try { return new Date(iso).toLocaleString(); }
  catch { return iso; }
}

function downloadJson(data: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 150);
}

const STATUS_LABELS: Record<number, string> = { 0: 'Pending', 1: 'Responded', 2: 'Omitted', 3: 'Expired' };
const STATUS_CSS:    Record<number, string> = {
  0: 'mon-inst-status--pending',
  1: 'mon-inst-status--responded',
  2: 'mon-inst-status--omitted',
  3: 'mon-inst-status--expired',
};

// ─── Stats card ───────────────────────────────────────────────────────────────

function StatCard({ icon, label, value, sub }: {
  icon: string; label: string; value: string | number; sub?: string;
}) {
  return (
    <div className="mon-stat">
      <i className={`fas ${icon} mon-stat-icon`} aria-hidden="true" />
      <div className="mon-stat-body">
        <div className="mon-stat-value">{value}</div>
        <div className="mon-stat-label">{label}</div>
        {sub && <div className="mon-stat-sub">{sub}</div>}
      </div>
    </div>
  );
}

// ─── Instances modal ──────────────────────────────────────────────────────────

function InstancesModal({ userId, meetingId, tenantId, onClose }: {
  userId: string; meetingId: string; tenantId: string; onClose: () => void;
}) {
  const { t } = useTranslation();
  const [instances, setInstances] = useState<SurveyInstanceDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8_000);

    fetch(
      `${SURVEY_URL}/v1/survey-instances/pending?userId=${encodeURIComponent(userId)}&tenantId=${tenantId}`,
      { signal: controller.signal }
    )
      .then(r => { clearTimeout(timeout); if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data: { items?: SurveyInstanceDto[] } | SurveyInstanceDto[]) => {
        const items = Array.isArray(data) ? data : (data.items ?? []);
        setInstances(items.filter(i => i.meetingId === meetingId));
      })
      .catch(err => {
        clearTimeout(timeout);
        setError(err instanceof Error ? err.message : t('monitor.unknownError'));
      })
      .finally(() => setLoading(false));

    return () => { clearTimeout(timeout); controller.abort(); };
  }, [userId, meetingId, tenantId, t]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="mon-modal" onClick={e => e.stopPropagation()}>
        <div className="mon-modal-header">
          <span className="mon-modal-title">
            <i className="fas fa-envelope" aria-hidden="true" />
            {t('monitor.instancesTitle')}
          </span>
          <button className="mon-modal-close" onClick={onClose} type="button" aria-label={t('monitor.instancesClose')}>
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="mon-modal-body">
          <p className="mon-modal-meta">
            {t('monitor.colUserId')}: <span title={userId}>{shortId(userId)}</span>
            {' · '}
            {t('monitor.colMeetingId')}: <span title={meetingId}>{shortId(meetingId)}</span>
          </p>

          {loading && (
            <div className="mon-loading"><i className="fas fa-spinner fa-spin" /> {t('monitor.instancesLoading')}</div>
          )}

          {error && (
            <div className="mon-error"><i className="fas fa-exclamation-triangle" /> {error}</div>
          )}

          {!loading && !error && instances.length === 0 && (
            <div className="mon-no-participants">{t('monitor.instancesEmpty')}</div>
          )}

          {instances.length > 0 && (
            <table className="mon-inst-table">
              <thead>
                <tr>
                  <th>{t('monitor.instancesSurvey')}</th>
                  <th>{t('monitor.instancesStatus')}</th>
                  <th>{t('monitor.instancesExpires')}</th>
                </tr>
              </thead>
              <tbody>
                {instances.map(inst => (
                  <tr key={inst.id}>
                    <td title={inst.id}>{inst.surveyName}</td>
                    <td className={STATUS_CSS[inst.status] ?? ''}>{STATUS_LABELS[inst.status] ?? inst.status}</td>
                    <td>{fmtDate(inst.expiresAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="mon-modal-footer">
          <button
            className="mon-btn-download"
            type="button"
            onClick={() => downloadJson(instances, `instances-${shortId(userId)}-${shortId(meetingId)}.json`)}
            disabled={instances.length === 0}
          >
            <i className="fas fa-download" /> {t('monitor.instancesDownload')}
          </button>
          <button className="mon-btn-close-modal" type="button" onClick={onClose}>
            {t('monitor.instancesClose')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Event row ────────────────────────────────────────────────────────────────

function EventRow({ ev }: { ev: PipelineEvent }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [modalUserId, setModalUserId] = useState<string | null>(null);

  return (
    <>
      <tr
        className={`mon-row mon-row--${ev.isMeetingEligible ? 'ok' : 'skip'}`}
        onClick={() => setExpanded(e => !e)}
        style={{ cursor: 'pointer' }}
      >
        <td className="mon-row-id" title={ev.meetingId}>{shortId(ev.meetingId)}</td>
        <td>{fmtDate(ev.createdAt)}</td>
        <td className="mon-row-num">{ev.durationMinutes}min</td>
        <td className="mon-row-num">{ev.totalEvaluated}</td>
        <td className="mon-row-num">{ev.totalEligible}</td>
        <td className="mon-row-num">{ev.totalSampled}</td>
        <td>
          {ev.maxPaxApplied && (
            <span className="mon-badge mon-badge--warn" title={t('monitor.samplingApplied')}>
              <i className="fas fa-random" />
            </span>
          )}
        </td>
        <td>
          {ev.isMeetingEligible
            ? <span className="mon-badge mon-badge--ok"><i className="fas fa-check-circle" /> {t('monitor.eligible')}</span>
            : <span className="mon-badge mon-badge--skip" title={ev.meetingViolations.join('; ')}>
                <i className="fas fa-ban" /> {t('monitor.ineligible')}
              </span>}
        </td>
        <td>
          <i className={`fas fa-chevron-${expanded ? 'up' : 'down'} mon-chevron`} aria-hidden="true" />
        </td>
      </tr>

      {expanded && (
        <tr className="mon-detail-row">
          <td colSpan={9}>
            {ev.meetingViolations.length > 0 && (
              <div className="mon-violations">
                <i className="fas fa-exclamation-triangle" /> {ev.meetingViolations.join(' · ')}
              </div>
            )}
            <ParticipantDetailTable
              rows={ev.participantDetails}
              renderSampleCell={(row) => row.includedInSample
                ? (
                  <button
                    className="mon-badge-btn"
                    type="button"
                    title={t('monitor.instancesBtnTitle')}
                    onClick={(event) => {
                      event.stopPropagation();
                      setModalUserId(row.userId);
                    }}
                  >
                    <i className="fas fa-envelope" aria-hidden="true" /> {t('monitor.sampled')}
                  </button>
                )
                : '—'}
            />
          </td>
        </tr>
      )}

      {modalUserId && (
        <InstancesModal
          userId={modalUserId}
          meetingId={ev.meetingId}
          tenantId={ev.tenantId}
          onClose={() => setModalUserId(null)}
        />
      )}
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function MonitoreoTab() {
  const { t } = useTranslation();
  const { demoUser } = useUserContext();
  const [events, setEvents] = useState<PipelineEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<Date | null>(null);
  const [sortAsc, setSortAsc] = useState(false);
  const [recalcMsg, setRecalcMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const recalculate = useRecalculateResponseCounts();

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${MEETING_URL}/api/meetings/pipeline-events?tenantId=${demoUser.tenant}&limit=50`;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10_000);
      let res: Response;
      try {
        res = await fetch(url, { signal: controller.signal });
      } finally {
        clearTimeout(timeout);
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as PipelineEvent[];
      setEvents(Array.isArray(data) ? data : []);
      setLastFetch(new Date());
    } catch (err) {
      const msg = err instanceof Error
        ? (err.name === 'AbortError' ? t('monitor.timeout') : err.message)
        : t('monitor.unknownError');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [t, demoUser.tenant]);

  useEffect(() => { void fetchEvents(); }, [fetchEvents]);

  // ── Sort + Aggregate stats ───────────────────────────────────────────────────
  const sortedEvents = [...events].sort((a, b) => {
    const diff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    return sortAsc ? diff : -diff;
  });

  const total = events.length;
  const eligible = events.filter(e => e.isMeetingEligible).length;
  const eligiblePct = total > 0 ? Math.round((eligible / total) * 100) : 0;
  const samplingCount = events.filter(e => e.maxPaxApplied).length;
  const avgSampled = eligible > 0
    ? (events.filter(e => e.isMeetingEligible).reduce((s, e) => s + e.totalSampled, 0) / eligible).toFixed(1)
    : '—';

  return (
    <div className="mon-tab">
      {/* Header */}
      <header className="page-header">
        <div>
          <h2 className="page-title">
            <i className="fas fa-stream" aria-hidden="true" /> {t('monitor.title')}
          </h2>
          <p className="page-subtitle">{t('monitor.subtitle')}</p>
          {lastFetch && (
            <span className="mon-last-fetch">
              <i className="fas fa-clock" /> {t('monitor.lastFetch')}: {lastFetch.toLocaleTimeString()}
            </span>
          )}
        </div>
        <div className="page-toolbar">
          <button
            className="mon-btn-recalculate"
            onClick={() => {
              setRecalcMsg(null);
              recalculate.mutate(undefined, {
                onSuccess: (data) => setRecalcMsg({ ok: true, text: t('monitor.recalculateSuccess', { count: data.surveysUpdated }) }),
                onError: () => setRecalcMsg({ ok: false, text: t('monitor.recalculateError') }),
              });
            }}
            disabled={recalculate.isPending}
            title={t('monitor.recalculateTitle')}
            type="button"
          >
            <i className={`fas fa-calculator${recalculate.isPending ? ' fa-spin' : ''}`} aria-hidden="true" />
            {t('monitor.recalculate')}
          </button>
          <button
            className="mon-btn-refresh"
            onClick={() => void fetchEvents()}
            disabled={loading}
            type="button"
          >
            <i className={`fas fa-sync-alt${loading ? ' fa-spin' : ''}`} aria-hidden="true" />
            {t('monitor.refresh')}
          </button>
        </div>
      </header>

      {recalcMsg && (
        <div className={recalcMsg.ok ? 'mon-recalc-ok' : 'mon-error'}>
          <i className={`fas ${recalcMsg.ok ? 'fa-check-circle' : 'fa-exclamation-triangle'}`} /> {recalcMsg.text}
        </div>
      )}

      {/* Stats cards */}
      <div className="mon-stats">
        <StatCard icon="fa-list-ol"      label={t('monitor.statTotal')}      value={total} />
        <StatCard icon="fa-check-circle" label={t('monitor.statEligible')}   value={`${eligible} (${eligiblePct}%)`} />
        <StatCard icon="fa-envelope"     label={t('monitor.statAvgSampled')} value={avgSampled} sub={t('monitor.statAvgSampledSub')} />
        <StatCard icon="fa-random"       label={t('monitor.statSampling')}   value={samplingCount} sub={t('monitor.statSamplingSub')} />
      </div>

      {/* Error */}
      {error && (
        <div className="mon-error">
          <i className="fas fa-exclamation-triangle" /> {error}
        </div>
      )}

      {/* Loading */}
      {loading && !error && (
        <div className="mon-loading">
          <i className="fas fa-spinner fa-spin" /> {t('monitor.loading')}
        </div>
      )}

      {/* Events table */}
      {!loading && !error && events.length === 0 && (
        <div className="mon-empty">
          <i className="fas fa-inbox" />
          <p>{t('monitor.empty')}</p>
        </div>
      )}

      {events.length > 0 && (
        <div className="mon-table-wrap">
          <table className="mon-table">
            <thead>
              <tr>
                <th>{t('monitor.colMeetingId')}</th>
                <th>
                  <button
                    type="button"
                    className="mon-th-sort"
                    onClick={() => setSortAsc(v => !v)}
                    title={sortAsc ? t('monitor.sortDesc') : t('monitor.sortAsc')}
                  >
                    {t('monitor.colDate')}
                    <i className={`fas fa-sort-${sortAsc ? 'up' : 'down'}`} aria-hidden="true" />
                  </button>
                </th>
                <th>{t('monitor.colDuration')}</th>
                <th>{t('monitor.colEvaluated')}</th>
                <th>{t('monitor.colEligible')}</th>
                <th>{t('monitor.colSampled')}</th>
                <th>{t('monitor.colMaxPax')}</th>
                <th>{t('monitor.colResult')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedEvents.map(ev => <EventRow key={ev.id} ev={ev} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
